import React, { useState } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Mail, Phone, MapPin, Send, Building, TrendingUp } from 'lucide-react';
import { toast } from 'sonner';

const Contact = () => {
  const [contactForm, setContactForm] = useState({
    name: '',
    email: '',
    phone: '',
    message: ''
  });

  const [partnerForm, setPartnerForm] = useState({
    businessName: '',
    contactName: '',
    email: '',
    phone: '',
    location: '',
    type: '',
    message: ''
  });

  const handleContactSubmit = (e) => {
    e.preventDefault();
    toast.success('Message envoyé avec succès ! Nous vous répondrons sous 24h.');
    setContactForm({ name: '', email: '', phone: '', message: '' });
  };

  const handlePartnerSubmit = (e) => {
    e.preventDefault();
    toast.success('Demande de partenariat envoyée ! Notre équipe vous contactera sous 48h.');
    setPartnerForm({ businessName: '', contactName: '', email: '', phone: '', location: '', type: '', message: '' });
  };

  return (
    <section id="contact" className="py-20 bg-gradient-to-br from-gray-50 via-white to-yellow-50">
      <div className="container mx-auto px-6">
        <div className="text-center mb-16">
          <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-4">
            Contactez-Nous
          </h2>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto">
            Une question ? Un projet de partenariat ? Nous sommes à votre écoute
          </p>
        </div>

        <div className="grid lg:grid-cols-3 gap-12 max-w-6xl mx-auto">
          {/* Contact Info */}
          <div className="lg:col-span-1 space-y-6">
            <div className="bg-white p-6 rounded-2xl shadow-lg border border-gray-200 hover:border-yellow-300 transition-all duration-300">
              <div className="w-12 h-12 bg-yellow-100 rounded-lg flex items-center justify-center mb-4">
                <Mail className="w-6 h-6 text-yellow-600" />
              </div>
              <h3 className="font-bold text-gray-900 mb-2">Email</h3>
              <a href="mailto:societejya@gmail.com" className="text-gray-600 hover:text-yellow-600 transition-colors">
                societejya@gmail.com
              </a>
            </div>

            <div className="bg-white p-6 rounded-2xl shadow-lg border border-gray-200 hover:border-yellow-300 transition-all duration-300">
              <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center mb-4">
                <Phone className="w-6 h-6 text-green-600" />
              </div>
              <h3 className="font-bold text-gray-900 mb-2">Téléphone</h3>
              <a href="tel:+33781917718" className="text-gray-600 hover:text-green-600 transition-colors">
                07 81 91 77 18
              </a>
            </div>

            <div className="bg-white p-6 rounded-2xl shadow-lg border border-gray-200 hover:border-yellow-300 transition-all duration-300">
              <div className="w-12 h-12 bg-yellow-100 rounded-lg flex items-center justify-center mb-4">
                <MapPin className="w-6 h-6 text-yellow-600" />
              </div>
              <h3 className="font-bold text-gray-900 mb-2">Adresse</h3>
              <p className="text-gray-600">
                64 Rue Belle de Mai<br />
                13003 Marseille, France
              </p>
            </div>
          </div>

          {/* Forms */}
          <div className="lg:col-span-2">
            <Tabs defaultValue="contact" className="w-full">
              <TabsList className="grid w-full grid-cols-2 mb-8 bg-gray-100 p-1 rounded-lg">
                <TabsTrigger value="contact" className="rounded-md data-[state=active]:bg-yellow-500 data-[state=active]:text-gray-900 font-semibold">
                  Contact Général
                </TabsTrigger>
                <TabsTrigger value="partner" className="rounded-md data-[state=active]:bg-green-500 data-[state=active]:text-white font-semibold">
                  Devenir Partenaire
                </TabsTrigger>
              </TabsList>

              {/* Contact Form */}
              <TabsContent value="contact">
                <form onSubmit={handleContactSubmit} className="bg-white p-8 rounded-2xl shadow-xl border border-gray-200 space-y-6">
                  <div>
                    <Label htmlFor="name" className="text-gray-900 font-semibold">
                      Nom complet *
                    </Label>
                    <Input
                      id="name"
                      type="text"
                      required
                      value={contactForm.name}
                      onChange={(e) => setContactForm({ ...contactForm, name: e.target.value })}
                      className="mt-2 border-gray-300 focus:border-yellow-500 focus:ring-yellow-500"
                      placeholder="Jean Dupont"
                    />
                  </div>

                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <Label htmlFor="email" className="text-gray-900 font-semibold">
                        Email *
                      </Label>
                      <Input
                        id="email"
                        type="email"
                        required
                        value={contactForm.email}
                        onChange={(e) => setContactForm({ ...contactForm, email: e.target.value })}
                        className="mt-2 border-gray-300 focus:border-yellow-500 focus:ring-yellow-500"
                        placeholder="jean@example.com"
                      />
                    </div>

                    <div>
                      <Label htmlFor="phone" className="text-gray-900 font-semibold">
                        Téléphone
                      </Label>
                      <Input
                        id="phone"
                        type="tel"
                        value={contactForm.phone}
                        onChange={(e) => setContactForm({ ...contactForm, phone: e.target.value })}
                        className="mt-2 border-gray-300 focus:border-yellow-500 focus:ring-yellow-500"
                        placeholder="+33 6 12 34 56 78"
                      />
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="message" className="text-gray-900 font-semibold">
                      Message *
                    </Label>
                    <Textarea
                      id="message"
                      required
                      value={contactForm.message}
                      onChange={(e) => setContactForm({ ...contactForm, message: e.target.value })}
                      className="mt-2 border-gray-300 focus:border-yellow-500 focus:ring-yellow-500 min-h-[150px]"
                      placeholder="Décrivez votre demande..."
                    />
                  </div>

                  <Button 
                    type="submit"
                    className="w-full bg-yellow-500 hover:bg-yellow-600 text-gray-900 font-bold text-lg py-6 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300"
                  >
                    <Send className="w-5 h-5 mr-2" />
                    Envoyer le Message
                  </Button>
                </form>
              </TabsContent>

              {/* Partner Form */}
              <TabsContent value="partner">
                <form onSubmit={handlePartnerSubmit} className="bg-white p-8 rounded-2xl shadow-xl border border-gray-200 space-y-6">
                  <div>
                    <Label htmlFor="businessName" className="text-gray-900 font-semibold">
                      Nom de l'établissement *
                    </Label>
                    <Input
                      id="businessName"
                      type="text"
                      required
                      value={partnerForm.businessName}
                      onChange={(e) => setPartnerForm({ ...partnerForm, businessName: e.target.value })}
                      className="mt-2 border-gray-300 focus:border-green-500 focus:ring-green-500"
                      placeholder="Café Le Central"
                    />
                  </div>

                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <Label htmlFor="contactName" className="text-gray-900 font-semibold">
                        Nom du contact *
                      </Label>
                      <Input
                        id="contactName"
                        type="text"
                        required
                        value={partnerForm.contactName}
                        onChange={(e) => setPartnerForm({ ...partnerForm, contactName: e.target.value })}
                        className="mt-2 border-gray-300 focus:border-green-500 focus:ring-green-500"
                        placeholder="Marie Martin"
                      />
                    </div>

                    <div>
                      <Label htmlFor="partnerEmail" className="text-gray-900 font-semibold">
                        Email *
                      </Label>
                      <Input
                        id="partnerEmail"
                        type="email"
                        required
                        value={partnerForm.email}
                        onChange={(e) => setPartnerForm({ ...partnerForm, email: e.target.value })}
                        className="mt-2 border-gray-300 focus:border-green-500 focus:ring-green-500"
                        placeholder="marie@example.com"
                      />
                    </div>
                  </div>

                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <Label htmlFor="partnerPhone" className="text-gray-900 font-semibold">
                        Téléphone *
                      </Label>
                      <Input
                        id="partnerPhone"
                        type="tel"
                        required
                        value={partnerForm.phone}
                        onChange={(e) => setPartnerForm({ ...partnerForm, phone: e.target.value })}
                        className="mt-2 border-gray-300 focus:border-green-500 focus:ring-green-500"
                        placeholder="+33 6 12 34 56 78"
                      />
                    </div>

                    <div>
                      <Label htmlFor="type" className="text-gray-900 font-semibold">
                        Type d'établissement *
                      </Label>
                      <Input
                        id="type"
                        type="text"
                        required
                        value={partnerForm.type}
                        onChange={(e) => setPartnerForm({ ...partnerForm, type: e.target.value })}
                        className="mt-2 border-gray-300 focus:border-green-500 focus:ring-green-500"
                        placeholder="Café, Centre commercial, etc."
                      />
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="location" className="text-gray-900 font-semibold">
                      Adresse de l'établissement *
                    </Label>
                    <Input
                      id="location"
                      type="text"
                      required
                      value={partnerForm.location}
                      onChange={(e) => setPartnerForm({ ...partnerForm, location: e.target.value })}
                      className="mt-2 border-gray-300 focus:border-green-500 focus:ring-green-500"
                      placeholder="45 Rue de la République, 75011 Paris"
                    />
                  </div>

                  <div>
                    <Label htmlFor="partnerMessage" className="text-gray-900 font-semibold">
                      Message (optionnel)
                    </Label>
                    <Textarea
                      id="partnerMessage"
                      value={partnerForm.message}
                      onChange={(e) => setPartnerForm({ ...partnerForm, message: e.target.value })}
                      className="mt-2 border-gray-300 focus:border-green-500 focus:ring-green-500 min-h-[120px]"
                      placeholder="Parlez-nous de votre établissement et de votre projet..."
                    />
                  </div>

                  <Button 
                    type="submit"
                    className="w-full bg-green-500 hover:bg-green-600 text-white font-bold text-lg py-6 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300"
                  >
                    <Building className="w-5 h-5 mr-2" />
                    Soumettre ma Candidature
                  </Button>
                  
                  <div className="flex items-center justify-center gap-2">
                    <TrendingUp className="w-4 h-4 text-green-600" />
                    <p className="text-sm text-gray-600 text-center">
                      Gagnez 30% du chiffre d'affaires de votre secteur
                    </p>
                  </div>
                </form>
              </TabsContent>
            </Tabs>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Contact;
